import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2 } from "lucide-react";
import { getDataQuality } from "@/lib/api";
import { CsvButton, Empty, TH, TR, downloadCsv, errMsg, money, openInGym } from "@/components/accounts/shared";

// Revenue records across every gym that need a person to look at them. Read-only: fixes are made
// in the gym's own page (the row's link switches to that gym and searches for the member).
export default function DataQuality({ country, gym }) {
  const [data, setData] = useState(null);
  const [check, setCheck] = useState(null);
  const [since, setSince] = useState("");

  useEffect(() => {
    let live = true;
    getDataQuality({ since: since || undefined, country: country || undefined, facility_id: gym || undefined })
      .then((d) => { if (live) { setData(d); setSince((s) => s || d.since); } })
      .catch((e) => toast.error(errMsg(e, "Could not load the checks")));
    return () => { live = false; };
  }, [since, country, gym]);

  const rows = useMemo(() => (data?.rows || []).filter((r) => !check || r.check === check), [data, check]);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const label = Object.fromEntries(data.checks.map((c) => [c.id, c.label]));
  const csv = () => downloadCsv("data-quality.csv", ["Check", "Gym", "Date", "Member", "Detail", "Amount", "Record ID"],
    rows.map((r) => [label[r.check], r.gym, r.date, r.member, r.detail, r.amount, r.record_id]));

  return (
    <div className="space-y-4" data-testid="accounts-quality">
      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        <label className="flex items-center gap-2">Records from
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
            className="bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-zinc-200" data-testid="quality-since" />
        </label>
        <span>{data.total} to look at across {new Set(data.rows.map((r) => r.gym)).size} gym(s). Catalog prices are ex-tax, so sales are compared with price + tax.</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {data.checks.map((c) => (
          <button key={c.id} onClick={() => setCheck(check === c.id ? null : c.id)} data-testid={`quality-check-${c.id}`}
            className={`card-surface text-left px-3 py-2.5 transition-colors ${check === c.id ? "border-blue-500/60" : ""} ${c.count === 0 ? "opacity-60" : ""}`}>
            <div className={`text-xl font-semibold tabular-nums ${c.count ? (c.area === "money" ? "text-rose-300" : "text-amber-300") : "text-emerald-300"}`}>{c.count}</div>
            <div className="text-xs text-zinc-300">{c.label}</div>
            {c.count > 0 && <div className="text-[10px] text-zinc-500 truncate">{c.gyms.join(", ")}</div>}
          </button>
        ))}
      </div>
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-zinc-100">{check ? label[check] : "Everything"} <span className="text-zinc-500 font-normal">({rows.length})</span></div>
          <CsvButton onClick={csv} disabled={!rows.length} testid="quality-csv" />
        </div>
        {rows.length === 0 ? <Empty>Nothing to fix here.</Empty> : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className={TH}>
              {!check && <th className="px-3 py-2 font-medium">Check</th>}
              <th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Member</th><th className="px-3 py-2 font-medium">What's wrong</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {rows.slice(0, 500).map((r, i) => (
                <tr key={`${r.check}:${r.record_id}:${i}`} className={TR}>
                  {!check && <td className="px-3 py-2 text-xs text-zinc-400">{label[r.check]}</td>}
                  <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                  <td className="px-3 py-2 text-zinc-400 tabular-nums">{r.date}</td>
                  <td className="px-3 py-2 text-zinc-200">{r.member}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{r.detail}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r, r.amount)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openInGym(r.link)} className="text-blue-300 hover:text-blue-200 text-xs inline-flex items-center gap-1" data-testid="quality-open">
                      Open <ExternalLink className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > 500 && <div className="px-4 py-2 text-xs text-zinc-500">Showing 500 of {rows.length}. Download the CSV for all.</div>}
      </div>
    </div>
  );
}
